import React, { useEffect, useState } from 'react';
import { Phone, PhoneOff, PhoneForwarded, Mic, MicOff, X, User } from 'lucide-react';

const IncomingCallPopup = ({ 
  incomingCall, 
  activeCall,
  isConnected,
  isMuted,
  onAccept, 
  onReject,
  onIgnore, // New: Send to voicemail/VAPI immediately
  onEndCall,
  onToggleMute 
}) => {
  const [callDuration, setCallDuration] = useState(0);
  const [callerInfo, setCallerInfo] = useState({ name: 'Unknown', phone: '' });
  const [isOutgoing, setIsOutgoing] = useState(false);

  // Extract caller info from the call
  useEffect(() => {
    if (incomingCall) {
      const fromNumber = incomingCall.parameters?.From || 'Unknown';
      setCallerInfo({
        name: fromNumber,
        phone: fromNumber
      });
      setIsOutgoing(false);
    } else if (activeCall && !incomingCall) {
      // This is an outgoing call
      const toNumber = activeCall.parameters?.To || activeCall.customParameters?.get?.('To') || 'Calling...';
      setCallerInfo({
        name: toNumber,
        phone: toNumber
      });
      setIsOutgoing(true);
    }
  }, [incomingCall, activeCall]);

  // Track call duration when connected
  useEffect(() => {
    let interval;
    if (isConnected && activeCall) {
      setCallDuration(0);
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isConnected, activeCall]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleIgnore = () => {
    // Ignore sends to voicemail (VAPI)
    if (onIgnore) {
      onIgnore();
    } else if (onReject) {
      // Fallback to reject if no ignore handler
      onReject();
    }
  };

  // Show incoming call popup - SMALL CORNER POPUP
  if (incomingCall && !isConnected) {
    return (
      <div 
        className="fixed top-4 right-4 z-[9999] animate-slide-in-right" 
        data-testid="incoming-call-popup"
      >
        <div className="bg-gray-900 border border-green-500/50 rounded-xl w-[300px] overflow-hidden shadow-2xl shadow-green-500/20">
          {/* Pulsing border effect */}
          <div className="absolute inset-0 rounded-xl border-2 border-green-500 animate-pulse pointer-events-none" />
          
          {/* Header with caller info */}
          <div className="bg-gradient-to-r from-green-600/90 to-green-500/90 p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <User size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/70 text-[10px] uppercase tracking-wider font-medium">
                  Incoming Call
                </p>
                <p className="text-white font-semibold truncate">{callerInfo.name}</p>
                <p className="text-white/80 text-xs">{callerInfo.phone}</p>
              </div>
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                <Phone size={16} className="text-white animate-bounce" />
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="p-3 bg-gray-900/95 flex items-center justify-between gap-2">
            {/* Ignore/Send to Voicemail - call continues, forwards to VAPI */}
            <button
              onClick={handleIgnore}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-600/80 hover:bg-purple-600 rounded-lg transition-all text-white text-sm"
              data-testid="ignore-call-btn"
              title="Send to AI Voicemail (Klara)"
            >
              <PhoneForwarded size={16} />
              <span>Voicemail</span>
            </button>
            
            {/* Decline - hangs up immediately, no voicemail */}
            <button
              onClick={onReject}
              className="w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all hover:scale-105"
              data-testid="reject-call-btn"
              title="Hang up (no voicemail)"
            >
              <PhoneOff size={18} className="text-white" />
            </button>
            
            {/* Accept */}
            <button
              onClick={onAccept}
              className="w-10 h-10 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center transition-all hover:scale-105 animate-pulse"
              data-testid="accept-call-btn"
              title="Answer call"
            >
              <Phone size={18} className="text-white" />
            </button>
          </div>
          
          {/* Tip text */}
          <div className="px-3 pb-2 text-center">
            <span className="text-[10px] text-gray-500">
              Voicemail → AI (Klara) | Red → Hang up
            </span>
          </div>
        </div>
        
        {/* Custom slide-in animation */}
        <style>{`
          @keyframes slide-in-right {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out;
          }
        `}</style>
      </div>
    );
  }

  // Show outgoing call connecting UI (before isConnected is true)
  if (activeCall && !isConnected && !incomingCall) {
    return (
      <div className="fixed bottom-6 right-6 z-[9999]" data-testid="outgoing-call-widget">
        <div className="bg-gray-900 border border-blue-500/50 rounded-xl w-[280px] overflow-hidden shadow-2xl">
          {/* Calling Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <Phone size={18} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm truncate">{callerInfo.name}</p>
                <p className="text-white/80 text-xs animate-pulse">Calling...</p>
              </div>
            </div>
          </div>
          
          {/* Cancel Call */}
          <div className="p-3 flex justify-center bg-gray-900">
            <button
              onClick={onEndCall}
              className="w-12 h-12 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all"
              data-testid="cancel-call-btn"
            >
              <PhoneOff size={20} className="text-white" />
            </button>
          </div>
          
          {/* Status indicator */}
          <div className="px-3 pb-2 text-center">
            <span className="inline-flex items-center gap-1.5 text-[10px] text-blue-400">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
              Connecting via Twilio
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show active call UI - Compact widget
  if (isConnected && activeCall) {
    return (
      <div className="fixed bottom-6 right-6 z-[9999]" data-testid="active-call-widget">
        <div className="bg-gray-900 border border-green-500/50 rounded-xl w-[280px] overflow-hidden shadow-2xl">
          {/* Call Status Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-500 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                  <Phone size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm truncate max-w-[120px]">{callerInfo.name}</p>
                  <p className="text-white/80 text-[10px]">{callerInfo.phone}</p>
                </div>
              </div>
              <div className="text-white font-mono text-base bg-white/10 px-2 py-0.5 rounded">
                {formatDuration(callDuration)}
              </div>
            </div>
          </div>
          
          {/* Call Controls */}
          <div className="p-3 flex gap-3 justify-center bg-gray-900">
            <button
              onClick={onToggleMute}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isMuted 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              data-testid="mute-btn"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <MicOff size={18} className="text-white" />
              ) : (
                <Mic size={18} className="text-white" />
              )}
            </button>
            
            <button
              onClick={onEndCall}
              className="w-10 h-10 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all"
              data-testid="end-call-btn"
              title="End call"
            >
              <PhoneOff size={18} className="text-white" />
            </button>
          </div>
          
          {/* Connected indicator */}
          <div className="px-3 pb-2 text-center">
            <span className="inline-flex items-center gap-1.5 text-[10px] text-green-400">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Connected
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default IncomingCallPopup;
